/**
 * The Super Admin console's frame.
 *
 * Deliberately not `AppShell`. Everything in that topbar answers "which store
 * am I in?" — the switcher, the wallet, the plan, the store's notification
 * bell — and on a console that spans every merchant each of those is either
 * empty or quietly showing the operator's own account. Same design system,
 * same `.shell` styles; no merchant chrome.
 */

import type { ReactNode } from 'react'
import { useAuth } from '../hooks/useAuth.js'
import { AccountMenu, Sidebar, TopbarSearch, useRail } from './shellParts.js'
import { adminNav } from './nav.js'
import { MenuIcon, ShieldIcon } from './icons.js'

export function SuperAdminLayout({ children }: { children: ReactNode }): ReactNode {
  const { user } = useAuth()
  const rail = useRail()

  return (
    <div className={['shell', 'shell--admin', ...rail.classes].join(' ')}>
      <Sidebar sections={adminNav} homeTo="/admin" />
      <button type="button" className="sidebar__scrim" aria-label="Close navigation" onClick={rail.close} />

      <div className="shell__main">
        <div className="admin-strip">
          <ShieldIcon size={14} />
          <span>Super Admin — you are viewing platform-wide data across every merchant.</span>
        </div>

        <header className="topbar">
          <button type="button" className="icon-btn" onClick={rail.toggle} aria-label="Toggle navigation">
            <MenuIcon size={20} />
          </button>

          <TopbarSearch sections={adminNav} placeholder="Search admin pages…" />

          <div className="topbar__right">
            <AccountMenu
              name={user?.name ?? 'Account'}
              email={user?.email ?? ''}
              role="Super Admin"
              profileTo="/admin/profile"
              settingsTo="/admin/settings"
            />
          </div>
        </header>

        <main className="shell__body">{children}</main>
      </div>
    </div>
  )
}
